import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../services/api';
import { useUser } from '../../../contexts/userContext';
import './style.css';

const UFS = ['', 'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

export default function CommercialAccounts() {
  const { user } = useUser();
  const [data, setData] = useState({ contas: [], supervisores: [] });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!user?.empresa_id) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/commercial-accounts', { params: { empresa_id: user.empresa_id } });
      setData(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Não foi possível carregar as contas comerciais.');
    } finally {
      setLoading(false);
    }
  }, [user?.empresa_id]);

  useEffect(() => { load(); }, [load]);

  const accounts = useMemo(() => data.contas.filter((account) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return `${account.nome} ${account.cpf_cnpj} ${account.situacoes.join(' ')}`.toLowerCase().includes(term);
  }), [data.contas, search]);

  const change = (accountId, field, value) => {
    setData((current) => ({
      ...current,
      contas: current.contas.map((account) => account.id === accountId ? { ...account, [field]: value } : account),
    }));
  };

  const save = async (account) => {
    setSaving(account.id);
    setError('');
    try {
      await api.patch(`/commercial-accounts/${account.id}`, {
        empresa_id: user.empresa_id,
        primary_supervisor_user_id: account.primary_supervisor_user_id ? Number(account.primary_supervisor_user_id) : null,
        primary_uf: account.primary_uf || null,
      });
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Não foi possível salvar a conta comercial.');
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="commercial-accounts-panel">Carregando contas comerciais...</div>;

  return (
    <div className="commercial-accounts-panel">
      <div className="commercial-accounts-header">
        <div>
          <h2>Empresas comerciais</h2>
          <p>Supervisor e UF principais por CNPJ. Usuários inativos permanecem visíveis.</p>
        </div>
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar empresa, documento ou pendência" />
      </div>
      {error && <div className="commercial-accounts-error">{error}</div>}
      <div className="commercial-accounts-list">
        {accounts.map((account) => (
          <section className="commercial-account-card" key={account.id}>
            <div className="commercial-account-title">
              <div><strong>{account.nome}</strong><span>{account.cpf_cnpj} · {account.status}</span></div>
              <div className="commercial-account-badges">
                {account.situacoes.map((item) => <span key={item}>{item.replaceAll('_', ' ')}</span>)}
              </div>
            </div>
            <div className="commercial-account-fields">
              <label>Supervisor principal
                <select value={account.primary_supervisor_user_id || ''} onChange={(event) => change(account.id, 'primary_supervisor_user_id', event.target.value)}>
                  <option value="">Sem responsável principal</option>
                  {data.supervisores.map((supervisor) => <option key={supervisor.id} value={supervisor.id}>{supervisor.nome}{supervisor.is_active ? '' : ' (inativo)'}</option>)}
                </select>
              </label>
              <label>UF principal
                <select value={account.primary_uf || ''} onChange={(event) => change(account.id, 'primary_uf', event.target.value)}>
                  {UFS.map((uf) => <option key={uf || 'empty'} value={uf}>{uf || 'Sem UF principal'}</option>)}
                </select>
              </label>
              <button disabled={saving === account.id} onClick={() => save(account)}>{saving === account.id ? 'Salvando...' : 'Salvar'}</button>
            </div>
            <div className="commercial-account-resolution">Atual: {account.supervisor_resolvido_nome || 'Não atribuído'} ({account.supervisor_resolvido_basis}) · UFs encontradas: {account.ufs.join(', ') || 'nenhuma'}</div>
            <div className="commercial-account-members">
              {account.usuarios.map((member) => (
                <div key={member.id} className={member.is_active ? '' : 'inactive'}>
                  <strong>{member.nome}</strong>
                  <span>{member.is_active ? 'Ativo' : 'Inativo'} · {member.uf || 'sem UF'}</span>
                  <small>Supervisores: {member.supervisores.map((supervisor) => supervisor.nome).join(', ') || 'nenhum'}</small>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
